// envTune — shared "lighting look" of an environment, persisted server-side.
//
// Problem this solves (2026-07-16): every LiveTune panel knob lived ONLY in
// this browser's localStorage (`aos-livetune-v3`). Two artists looking at the
// same environment saw different lighting (one had envMapIntensity 0.35
// stored, the other 1.5) and "Copy URL" couldn't reproduce a look because the
// lighting knobs aren't in the URL vocabulary and localStorage wins on load.
//
// Fix: each environment_assets row carries a `default_tune` jsonb — the
// authored lighting for that environment, sibling of `default_pose`. Applying
// an environment applies its saved tune to everyone; "Save tune for everyone"
// in the LiveTunePanel PATCHes it back. Server is the source of truth
// (CLAUDE.md); localStorage stays what it always was — a local scratchpad.
//
// What is shared (TUNE_SLICES + camera minus blend): the slices that define
// how the scene LOOKS, plus the camera's fov/target/cage/seeThrough/follow —
// everything a "Save for everyone" should carry to a fresh viewer. What
// stays local: debug (mirror/probe dev oracles — sharing these would pop a
// mirror ball into every other viewer's scene), character.forceToon (a
// boot-time override, not a look), environment pose (already covered by
// default_pose), and camera.blend (the in-flight ease duration of whatever
// move is CURRENTLY happening — transient per-edit state, not a setting).

// Explicit .js extension: this module is imported by node:test directly
// (tests/unit/envTune.test.mjs), and Node ESM requires the extension.
import { liveTuneStore, DEFAULTS } from './liveTuneStore.js'

// `backdrop` (sky/ground colors) is shared: it's part of how a scene LOOKS, and
// two artists on the same environment should agree on it. Environments saved
// before it existed simply carry no `backdrop` key — applyTuneForEnvironment
// merges DEFAULTS ⊕ saved, so they resolve to the (off) defaults and the
// baseline it records includes them, i.e. no false "not shared" badge.
export const TUNE_SLICES = ['lighting', 'shadow', 'spotlight', 'spotlight2', 'spotlight3', 'hemisphere', 'backdrop', 'torch', 'pbr', 'pbrSkin']

function clone(v) {
  if (Array.isArray(v)) return v.map(clone)
  if (v && typeof v === 'object') {
    const out = {}
    for (const k of Object.keys(v)) out[k] = clone(v[k])
    return out
  }
  return v
}

function merge(base, patch) {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return patch
  const out = { ...base }
  for (const k of Object.keys(patch)) {
    if (patch[k] && typeof patch[k] === 'object' && !Array.isArray(patch[k])
        && base && typeof base[k] === 'object' && !Array.isArray(base[k])) {
      out[k] = merge(base[k], patch[k])
    } else {
      out[k] = patch[k]
    }
  }
  return out
}

// Resolve the saved tune off an asset row in any of the shapes the app passes
// around (assetRegistry entry, raw AAM DTO, CustomAssetManager row).
// Returns null when the asset has no saved tune ('{}' or absent) — callers
// treat null as "nothing authored yet", NOT as "reset to defaults".
export function extractTuneFromAsset(asset) {
  if (!asset) return null
  const t = asset.defaultTune ?? asset.default_tune
    ?? asset.raw?.defaultTune ?? asset.raw?.default_tune
  if (!t || typeof t !== 'object' || Array.isArray(t)) return null
  return Object.keys(t).length ? t : null
}

// The camera fields that are part of the shared "look" — everything in the
// camera slice EXCEPT `blend` (see the module header). Read by collectTune
// (snapshot a live, always-fully-shaped camera object) and mergeCameraTune
// (reconstruct one from a possibly-partial saved/baseline tune).
const CAMERA_SHARED_KEYS = ['fov', 'target', 'cage', 'seeThrough', 'follow']

function collectCamera(cam) {
  const out = {}
  for (const k of CAMERA_SHARED_KEYS) out[k] = clone(cam[k])
  return out
}

// `fov` is a bare scalar leaf sitting alongside camera's other, object-valued
// shared fields, so it can't go through the generic merge() helper the way
// target/cage/seeThrough/follow do (merge() only knows how to fold an object
// patch onto an object base — handed a missing/undefined patch for a scalar
// base it would wrongly produce `{}` instead of falling back to the scalar
// default). Nullish-coalesce it directly instead.
function mergeCameraTune(tuneCamera) {
  const t = tuneCamera ?? {}
  return {
    fov: t.fov ?? DEFAULTS.camera.fov,
    target: merge(clone(DEFAULTS.camera.target), t.target ?? {}),
    cage: merge(clone(DEFAULTS.camera.cage), t.cage ?? {}),
    seeThrough: merge(clone(DEFAULTS.camera.seeThrough), t.seeThrough ?? {}),
    follow: merge(clone(DEFAULTS.camera.follow), t.follow ?? {}),
  }
}

// Snapshot the CURRENT store state down to the shared vocabulary — this is
// exactly what "Save tune for everyone" persists, and what dirty-compare
// hashes. Unknown keys are dropped so a save can never smuggle local-only
// state (debug flags, camera.blend, etc.) into the shared row.
export function collectTune(state = liveTuneStore.getState()) {
  const out = {}
  for (const slice of TUNE_SLICES) out[slice] = clone(state[slice] ?? DEFAULTS[slice])
  out.camera = collectCamera(state.camera ?? DEFAULTS.camera)
  return out
}

// ── Baseline (what the server currently holds for the active environment) ──
// Module-level, mirroring the liveTuneStore singleton pattern. Set on every
// environment activation and on every successful save; the panel derives its
// DirtyBadge from serializeTune(collectTune()) !== getTuneBaseline().
let baseline = null // serialized string | null (null = env has no saved tune)

export function serializeTune(tune) {
  return JSON.stringify(tune)
}

export function getTuneBaseline() {
  return baseline
}

export function setTuneBaseline(tune) {
  baseline = tune == null ? null : serializeTune(tune)
}

// ── Diff & revert (panel save-UX) ──────────────────────────────────────────
// The panel's footer shows exactly which knobs differ from the shared look,
// with per-knob revert. "Shared look" = the saved baseline; an environment
// that has never been saved falls back to DEFAULTS — that IS what a fresh
// teammate would see, so diffing/discarding against it is truthful.

function effectiveBaselineTune() {
  if (baseline != null) {
    try { return JSON.parse(baseline) } catch { /* corrupted — fall through */ }
  }
  const out = {}
  for (const slice of TUNE_SLICES) out[slice] = clone(DEFAULTS[slice])
  out.camera = collectCamera(DEFAULTS.camera)
  return out
}

function flatten(obj, prefix = '', out = {}) {
  for (const k of Object.keys(obj ?? {})) {
    const v = obj[k]
    const path = prefix ? `${prefix}.${k}` : k
    if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, path, out)
    else out[path] = v
  }
  return out
}

// All knobs whose current value differs from the shared look, as
// [{ key: 'lighting.envMapIntensity', from: 1.5, to: 0.35 }, …].
export function diffTune(state = liveTuneStore.getState()) {
  const cur = flatten(collectTune(state))
  const base = flatten(effectiveBaselineTune())
  const keys = new Set([...Object.keys(cur), ...Object.keys(base)])
  const rows = []
  for (const key of keys) {
    if (JSON.stringify(cur[key]) !== JSON.stringify(base[key])) {
      rows.push({ key, from: base[key], to: cur[key] })
    }
  }
  rows.sort((a, b) => a.key.localeCompare(b.key))
  return rows
}

// Revert ONE knob to the shared look ('lighting.envMapIntensity' → nested
// setState patch). No-op for a path outside the shared vocabulary.
export function revertTuneKey(key) {
  const base = flatten(effectiveBaselineTune())
  if (!(key in base)) return false
  const parts = key.split('.')
  const patch = {}
  let node = patch
  for (let i = 0; i < parts.length - 1; i++) node = node[parts[i]] = {}
  node[parts[parts.length - 1]] = base[key]
  liveTuneStore.setState(patch)
  return true
}

// Revert EVERY shared knob to the shared look. Sends nothing to the server.
// Local-only state (debug, camera.blend, environment pose) is untouched.
export function discardTuneToBaseline() {
  const base = effectiveBaselineTune()
  const patch = {}
  for (const slice of TUNE_SLICES) {
    patch[slice] = merge(clone(DEFAULTS[slice]), base[slice] ?? {})
  }
  patch.camera = mergeCameraTune(base.camera)
  liveTuneStore.setState(patch)
}

// Apply an environment's saved tune to the live store. Called from BOTH
// activation paths (applyEnvironment + the LiveTunePanel dropdown), and from
// the `loadEnvironment` blueprint node via applyEnvironment's `authored` mode.
//
// Semantics:
//   tune present → each shared slice becomes DEFAULTS ⊕ saved — a full reset
//     of the shared vocabulary, so stale local drift (the 0.35 bug) cannot
//     survive an environment switch. Baseline := what was applied.
//   tune null/empty → the environment has no authored look; local knobs are
//     left untouched (never destroy an artist's in-progress tuning), and the
//     baseline clears so the panel shows "unsaved" against this env.
export function applyTuneForEnvironment(tune, { source = 'user' } = {}) {
  if (!tune) {
    setTuneBaseline(null)
    return false
  }
  const patch = {}
  for (const slice of TUNE_SLICES) {
    patch[slice] = merge(clone(DEFAULTS[slice]), tune[slice] ?? {})
  }
  patch.camera = mergeCameraTune(tune.camera)
  // Anything but a human pick ('blueprint' node, `?environment=` link) applies
  // live without persisting, so an environment's authored look never lands in
  // the dev's own settings (see tuneAuthored.js). A pick — content browser,
  // inspector, panel dropdown — persists as before.
  if (source === 'user') liveTuneStore.setState(patch)
  else liveTuneStore.setStateAuthored(patch, { source })
  setTuneBaseline(collectTune(liveTuneStore.getState()))
  return true
}
